import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Switch } from "@/components/ui/switch";
import { getConsent, gpcEnabled, onConsentChange, setConsent, type ConsentState } from "@/lib/consent";

/**
 * Per-category consent controls. Used inside the banner ("Customize") and on
 * /privacy-choices/. Renders nothing until mounted: the choice lives in the
 * visitor's browser, so prerendered HTML can't know it.
 */
export default function ConsentPreferences({ onSaved }: { onSaved?: () => void }) {
  const [state, setState] = useState<ConsentState | null>(null);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [gpc, setGpc] = useState(false);

  useEffect(() => {
    const apply = (c: ConsentState) => { setState(c); setAnalytics(c.analytics); setMarketing(c.marketing); };
    apply(getConsent());
    setGpc(gpcEnabled());
    return onConsentChange(apply);
  }, []);

  if (!state) return null;

  const save = () => {
    setConsent({ analytics, marketing });
    setSaved(true);
    onSaved?.();
  };

  return (
    <div className="space-y-4 text-sm">
      <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
        <div>
          <p className="font-semibold">Strictly necessary</p>
          <p className="text-muted-foreground">Remembers this choice, which consent rules apply to your region, and keeps forms and the site working. Always on.</p>
        </div>
        <Switch checked disabled aria-label="Strictly necessary (always on)" />
      </div>
      <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
        <div>
          <p className="font-semibold">Analytics</p>
          <p className="text-muted-foreground">
            Google Analytics (via Google Tag Manager) and our own first-party analytics: pages visited, how you arrived,
            and — if you send us a form — which pages you viewed before it.{" "}
            <Link to="/lead-attribution-notice/" className="text-primary underline">How this works</Link>
          </p>
        </div>
        <Switch id="consent-analytics" checked={analytics} onCheckedChange={(v) => { setAnalytics(v); setSaved(false); }} aria-label="Analytics" />
      </div>
      <div className="flex items-start justify-between gap-4 rounded-md border border-border p-3">
        <div>
          <p className="font-semibold">Marketing</p>
          <p className="text-muted-foreground">
            Advertising measurement and remarketing cookies set through Google Tag Manager.
            {gpc && " Your browser sends a Global Privacy Control signal, so this stays off."}
          </p>
        </div>
        <Switch id="consent-marketing" checked={marketing && !gpc} disabled={gpc} onCheckedChange={(v) => { setMarketing(v); setSaved(false); }} aria-label="Marketing" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={save}
          className="rounded-md bg-primary px-4 py-2 font-semibold text-primary-foreground hover:bg-primary/90">
          Save choices
        </button>
        {saved && <span role="status" className="text-muted-foreground">Saved.</span>}
      </div>
    </div>
  );
}
